import turfDistance from '@turf/distance';
import { AlgoliaFilterKey, AlgoliaResultType } from 'types/Search/Algolia';
import { pointItemToLngLat } from './mapbox/sources/point_items';
import { LanguageSupportUtil } from './language_support_util';

// TODO https://alltrails.atlassian.net/browse/TD-300
const AlgoliaToFiltersMapping = {
  [AlgoliaFilterKey.Access]: AlgoliaFilterKey.Access,
  [AlgoliaFilterKey.Activities]: AlgoliaFilterKey.Activities,
  [AlgoliaFilterKey.Activity]: AlgoliaFilterKey.Activities,
  [AlgoliaFilterKey.Feature]: AlgoliaFilterKey.Features,
  [AlgoliaFilterKey.Features]: AlgoliaFilterKey.Features,
  [AlgoliaFilterKey.Suitability]: AlgoliaFilterKey.Access
};

const SearchResultsUtil = {
  createWeightedResultList(results, center, weightDistance) {
    const scale = weightDistance;
    const decay = 0.5;
    const magicSortFactor = -2 * (Math.pow(scale, 2) / (2 * Math.log(decay)));
    const centerLngLat = [center[1], center[0]];
    return results
      .map(result => {
        const distanceToCenter = turfDistance(centerLngLat, pointItemToLngLat(result)) * 1000;
        result.adjustedPopularity = result.popularity * Math.exp((-1 * Math.pow(distanceToCenter, 2)) / magicSortFactor);
        return result;
      })
      .sort((a, b) => b.adjustedPopularity - a.adjustedPopularity);
  },
  createCreatedAtDateDescResultList(results) {
    return results.sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0));
  },
  createPopularResultList(results) {
    return results.sort((a, b) => b.popularity - a.popularity);
  },
  createClosestResultList(results, center) {
    const centerLngLat = [center[1], center[0]];
    return results
      .map(result => {
        result.distanceToCenter = turfDistance(centerLngLat, pointItemToLngLat(result));
        return result;
      })
      .sort((a, b) => a.distanceToCenter - b.distanceToCenter);
  },
  sortByCustomListOrder(results, filters, getListItem) {
    if (!getListItem) {
      return results;
    }
    // favorite, completed, and custom list pages
    return results.sort((a, b) => {
      a = getListItem(a.type, a.ID);
      b = getListItem(b.type, b.ID);
      // descending order
      if (filters.sort['list-order'].value === -1) {
        return (b ? b.order : null) - (a ? a.order : null);
      }
      // ascendening order
      return (a ? a.order : null) - (b ? b.order : null);
    });
  },
  sortByName(results, filters) {
    return results.sort((a, b) => {
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();
      if (filters.sort.name.value === 1) {
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      }

      if (nameA < nameB) return 1;
      if (nameA > nameB) return -1;
      return 0;
    });
  },
  sortByDate(results, filters) {
    return results.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      if (filters.sort.date.value === -1) return dateA - dateB;
      return dateB - dateA;
    });
  },
  sortByRecentlyAdded(results, filters) {
    return results.sort((a, b) => {
      const dateA = new Date(a.list_item_created);
      const dateB = new Date(b.list_item_created);
      if (filters.sort['recently-added'].value === -1) return dateA - dateB;
      return dateB - dateA;
    });
  },
  applySortFunction(items, filters, center, weightDistance, getListItem = null) {
    const selectedFilterKey = Object.keys(filters.sort).filter(itemKey => {
      return filters.sort[itemKey].selected;
    })[0];
    if (selectedFilterKey) {
      if (selectedFilterKey == 'best-match') return this.createWeightedResultList(items, center, weightDistance);
      if (selectedFilterKey == 'most-popular') return this.createPopularResultList(items);
      if (selectedFilterKey == 'newly-added') return this.createCreatedAtDateDescResultList(items);
      if (selectedFilterKey == 'closest') return this.createClosestResultList(items, center);
      if (selectedFilterKey == 'list-order') return this.sortByCustomListOrder(items, filters, getListItem);
      if (selectedFilterKey == 'name') return this.sortByName(items, filters);
      if (selectedFilterKey == 'date') return this.sortByDate(items, filters);
      if (selectedFilterKey == 'recently-added') return this.sortByRecentlyAdded(items, filters);
    }
    return items;
  },
  formatTrailInfo(hit, languageRegionCode = 'en-US') {
    let resultString = '';

    if (hit.area_name && hit.area_name != '') {
      resultString += LanguageSupportUtil.findRecordFieldWithLocale(hit, 'area_name', languageRegionCode);
    }

    if (hit.city_name && hit.city_name != '') {
      resultString += (resultString == '' ? '' : ', ') + LanguageSupportUtil.findRecordFieldWithLocale(hit, 'city_name', languageRegionCode);
    }

    if (hit.state_name && hit.state_name != '') {
      resultString += (resultString == '' ? '' : ', ') + LanguageSupportUtil.findRecordFieldWithLocale(hit, 'state_name', languageRegionCode);
    }

    if (hit.country_name && hit.country_name != '') {
      resultString += (resultString == '' ? '' : ', ') + LanguageSupportUtil.findRecordFieldWithLocale(hit, 'country_name', languageRegionCode);
    }

    return resultString;
  },
  formatNonTrailInfo(hit, languageRegionCode = 'en-US') {
    let resultString = '';
    if (hit.type !== 'area' || (hit.area_type && hit.area_type === 'C')) {
      if (hit.city_name !== undefined && hit.city_name != '' && hit.city_name != null) {
        resultString += (resultString === '' ? '' : ', ') + LanguageSupportUtil.findRecordFieldWithLocale(hit, 'city_name', languageRegionCode);
      }
    }
    if (hit.state_name !== undefined && hit.state_name !== '' && hit.state_name != null) {
      resultString += (resultString == '' ? '' : ', ') + LanguageSupportUtil.findRecordFieldWithLocale(hit, 'state_name', languageRegionCode);
    }
    if (hit.country_name !== '' && hit.country_name != undefined) {
      resultString += (resultString == '' ? '' : ', ') + LanguageSupportUtil.findRecordFieldWithLocale(hit, 'country_name', languageRegionCode);
    }

    return resultString;
  },
  formatLocationInfo(hit, languageRegionCode = 'en-US') {
    return hit.type === 'trail' ? this.formatTrailInfo(hit, languageRegionCode) : this.formatNonTrailInfo(hit, languageRegionCode);
  },
  /**
   * createResultUrl returns a relative URL for a given hit. Downstream users
   * of this function should always assume the URL is a relative path.
   */
  createResultUrl(hit, languageRegionCode = 'en-US') {
    switch (hit.type) {
      case AlgoliaResultType.Track:
        return LanguageSupportUtil.wrapUrlSafe(`/explore/recording/${hit.slug}`, languageRegionCode);
      case AlgoliaResultType.Map:
        return LanguageSupportUtil.wrapUrlSafe(`/explore/map/${hit.slug}`, languageRegionCode);
      case AlgoliaResultType.List:
        return LanguageSupportUtil.wrapUrlSafe(`/lists/${hit.slug}`, languageRegionCode);
      case AlgoliaResultType.Filter:
        return LanguageSupportUtil.wrapUrlSafe(`/explore?${hit.slug}`, languageRegionCode);
      case AlgoliaResultType.POI:
        return LanguageSupportUtil.wrapUrlSafe(`/explore?lat=${hit._geoloc.lat}&lng=${hit._geoloc.lng}`, languageRegionCode);
      default:
        return LanguageSupportUtil.wrapUrlSafe(`/${hit.slug}`, languageRegionCode);
    }
  },

  /**
   * formatSearchResult formats an Algolia record as needed for our search results
   * @param {AlgoliaHit} hit
   * @param {string} languageRegionCode
   * @returns AlgoliaHit
   */
  formatSearchResult(hit, languageRegionCode = 'en-US') {
    const name = LanguageSupportUtil.findRecordFieldWithLocale(hit, 'name', languageRegionCode);
    let highlightedName = name;
    if (hit._highlightResult) {
      highlightedName = LanguageSupportUtil.findRecordFieldWithLocale(hit._highlightResult, 'name', languageRegionCode).value;
    }

    // TODO https://alltrails.atlassian.net/browse/TD-300
    return {
      filters: this.normalizeAlgoliaHitFilters(hit),
      location: this.formatLocationInfo(hit, languageRegionCode),
      url: this.createResultUrl(hit, languageRegionCode),
      name,
      highlightedName,
      type: hit.type,
      objectID: hit.objectID
    };
  },

  // TODO https://alltrails.atlassian.net/browse/TD-300
  normalizeAlgoliaHitFilters(hit) {
    if (hit.type !== AlgoliaResultType.Filter) {
      return undefined;
    }

    const filters = {};

    Object.keys(AlgoliaToFiltersMapping).forEach(oldKey => {
      if (hit.filters?.[oldKey]) {
        const newKey = AlgoliaToFiltersMapping[oldKey];
        filters[newKey] = hit.filters[oldKey];
      }
    });

    return filters;
  }
};

export { SearchResultsUtil };
